/**
 * Unified Modal Atom
 * @layer atom
 * @package @plerity/ui-core
 */

import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "../utils/cn";
import type { ModalProps } from "./Modal.types";

const sizeVariants: Record<string, string> = {
  sm: "max-w-[var(--app-modal-width-sm,28rem)]",
  md: "max-w-[var(--app-modal-width-md,32rem)]",
  lg: "max-w-[var(--app-modal-width-lg,42rem)]",
  xl: "max-w-[var(--app-modal-width-xl,56rem)]",
};

export function Modal({
  isOpen,
  onClose,
  title,
  ariaLabel,
  size = "md",
  children,
  footer,
  showCloseButton = true,
  className,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const triggerRef = useRef<Element | null>(null);

  const isVisible = isOpen;

  // Handle ESC key
  useEffect(() => {
    if (!isVisible) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isVisible, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isVisible) return;

    triggerRef.current = document.activeElement;

    const focusableSelectors = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ].join(", ");

    const firstFocusable = modalRef.current?.querySelector<HTMLElement>(focusableSelectors);
    firstFocusable?.focus();

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !modalRef.current) return;

      const focusable = Array.from(modalRef.current.querySelectorAll<HTMLElement>(focusableSelectors)).filter(
        (el) => !el.closest("[hidden]")
      );

      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleTab);
    return () => {
      document.removeEventListener("keydown", handleTab);
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    };
  }, [isVisible]);

  // Prevent body scroll
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isVisible]);

  if (!isVisible) return null;

  // Render through a portal to document.body so the overlay sits in the ROOT
  // stacking context. A `fixed` modal rendered inline is trapped by any
  // ancestor that creates a stacking context (transform, z-index, opacity, …)
  // and can be painted UNDER unrelated sibling content regardless of z-index.
  // z-[9999] aligns with the --z-modal token used across the apps.
  if (typeof document === "undefined") return null;

  const content = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop — click-outside-to-close; aria-hidden so keyboard users use ESC via useEffect */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" aria-hidden="true" onClick={onClose} />

      {/* Panel */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        className={cn(
          "relative z-10 mx-4 w-full max-h-[var(--app-modal-max-height,90vh)] overflow-y-auto",
          // Literal white/gray-800 surface — do NOT use `bg-surface` here. It emits
          // `background-color: var(--color-bg-surface)` with NO fallback, and that var
          // is stripped from the React SPA apps' Tailwind v4 build (`:root` blocks in
          // `@import`-ed colors.css are dropped). The literal ramp keeps fallbacks
          // (`var(--color-gray-800,#1e293b)`) so `dark:bg-gray-800` renders true-dark.
          "rounded-[var(--app-modal-radius,0.5rem)] bg-white shadow-xl dark:bg-gray-800",
          sizeVariants[size],
          className
        )}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            {title && (
              <h2 id={titleId} className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                aria-label="Close"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="px-6 py-4">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

Modal.displayName = "Modal";
